<!--
  APV contracts to encode — /apv-encode.

  On y arrive par le bouton « Encode » du rapport Harvesting du matin et, depuis
  le 2026-10-27, par le même bouton dans Harvesting 2.0 (tableau de bord, liste
  et fiche des APV) — toujours sans entrée de menu. `?apv=<code>` met cet APV
  en tête de la liste et le surligne.
  Admins et modérateurs : Florent, qui encode les réceptions, est modérateur.

  La liste est celle de la section « APV de înregistrat » du mail (APV sans
  réception d'achat). Pour chaque APV : on charge le contrat (PDF ou photos),
  Claude le lit (une à trois minutes), puis le volet de confirmation montre la
  réception telle qu'elle sera créée — fournisseur, « n° contrat/date »,
  partida, volume, prix — à côté des documents. Rien n'est écrit dans l'ERP
  avant « Confirm and create the reception ».

  « New contract » (2026-11-03, demande de Chady) : le contrat D'ABORD, quand
  SUMAL n'a pas encore l'APV. La ligne naît sans code ; Claude lit le code, la
  partida, le volume et le prix dans le contrat ; la réception se crée avec un
  produit « p.<partida> <parcelle> - <code> », que SUMAL retrouvera par le code.

  Interface en anglais, comme le reste de l'ERP. Composants hv2, feuille
  harvesting2.scss importée non scopée (la page vit hors du layout /harvesting2,
  donc sans son rideau PIN). Le vs-popup est toujours monté, à la fin, et la
  liste est dans un conteneur stable : Vuesax déplace le popup dans <body>.
-->
<template>
  <div id="apv-encode" class="hv2">
    <div>
      <hv-section title="APV contracts to encode" :sub="subtitle">
        <template slot="actions">
          <vs-button
            size="small"
            color="success"
            icon-pack="feather"
            icon="icon-file-plus"
            class="mr-2"
            :disabled="uploading.new !== undefined"
            title="The contract first: for an APV that SUMAL does not show yet. Claude reads the APV number in the contract."
            @click="pickFiles(null)">
            {{ uploading.new !== undefined ? `Uploading… ${uploading.new} %` : 'New contract' }}
          </vs-button>
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw" :disabled="loading" @click="load">
            Refresh
          </vs-button>
        </template>

        <div v-if="loading && !rows.length" key="loading" class="hv2-loading">
          <vs-progress indeterminate color="primary" class="mb-2" />
          Loading…
        </div>
        <div v-else-if="error" key="error" class="hv2-error">{{ error }}</div>
        <p v-if="focus && !loading && !error && !rowOf(focus)" key="focus-missing" class="hv2-sec-sub">
          APV {{ focus }} is not in this list: it already has its purchase reception, or it is outside the
          Harvesting report (no activity since the horizon, or dismissed).
        </p>
        <hv-empty
          v-else-if="!rows.length"
          key="empty"
          icon="CheckCircleIcon"
          title="Nothing to encode."
          text="Every APV has its purchase reception." />
        <vs-table v-else key="table" :data="rows" class="table-dark-inverted">
          <template slot="thead">
            <vs-th>APV</vs-th>
            <vs-th>Parcel</vs-th>
            <vs-th>Out / SUMAL stock</vs-th>
            <vs-th>Deadline</vs-th>
            <vs-th>Contract</vs-th>
            <vs-th></vs-th>
          </template>
          <template slot-scope="{ data }">
            <vs-tr v-for="r in data" :key="rowKey(r)" :class="{ 'apv-focus': r.apv_code && r.apv_code === focus }">
              <vs-td>
                <span v-if="r.apv_code" class="hv2-code apv-strong">{{ r.apv_code }}</span>
                <span v-else class="hv2-muted">Read from the contract…</span>
                <div v-if="r.contract_only && !r.in_sumal" class="mt-1">
                  <hv-pill
                    label="Not in SUMAL yet"
                    tone="warn"
                    title="Contract uploaded before SUMAL shows this APV: the product is created from the contract, and SUMAL will link to it by the 13-digit number." />
                </div>
                <div v-else-if="r.not_encoded" class="mt-1">
                  <hv-pill label="SUMAL only" tone="info" title="No product in XYLO yet: it is created with the reception." />
                </div>
              </vs-td>
              <vs-td>
                {{ r.label || (r.contract_only ? 'Contract' : DASH) }}
                <div class="hv2-muted">{{ placeLine(r) }}</div>
              </vs-td>
              <vs-td class="hv2-nowrap">
                <template v-if="r.contract_only"><span class="hv2-muted">{{ DASH }}</span></template>
                <template v-else>
                  {{ m3(r.m3_out) }} m³ <span class="hv2-muted">· {{ r.avize }} avize</span>
                  <div class="hv2-muted">stock {{ m3(r.sumal_stock_m3) }} m³</div>
                </template>
              </vs-td>
              <vs-td class="hv2-nowrap">
                <span :class="deadlineClass(r.days_to_expiry)">{{ deadlineText(r.days_to_expiry) }}</span>
                <div v-if="r.deadline" class="hv2-muted">{{ date(r.deadline) }}</div>
              </vs-td>
              <vs-td>
                <hv-pill :label="statusLabel(r)" :tone="statusTone(r)" :icon="statusIcon(r)" :title="(r.draft && r.draft.error) || ''" />
                <div v-if="uploading[rowKey(r)] !== undefined" class="hv2-muted mt-1">Uploading… {{ uploading[rowKey(r)] }} %</div>
                <div v-else-if="r.draft && r.draft.files.length" class="hv2-muted mt-1">{{ filesLine(r.draft) }}</div>
                <div v-if="r.draft && r.draft.status === 'failed' && r.draft.error" class="apv-error-line">{{ r.draft.error }}</div>
              </vs-td>
              <vs-td class="hv2-nowrap">
                <template v-if="!r.draft">
                  <vs-button size="small" icon-pack="feather" icon="icon-upload" :disabled="busyRow(r)" @click="pickFiles(r)">
                    Upload contract
                  </vs-button>
                </template>
                <template v-else-if="r.draft.status === 'reading'">
                  <span class="hv2-muted">Claude is reading the contract…</span>
                </template>
                <template v-else>
                  <vs-button
                    v-if="r.draft.status === 'ready'"
                    size="small"
                    color="success"
                    icon-pack="feather"
                    icon="icon-check-square"
                    class="mr-2"
                    @click="openReview(r)">Review &amp; confirm</vs-button>
                  <vs-button
                    v-if="r.draft.status === 'uploaded' || r.draft.status === 'failed'"
                    size="small"
                    icon-pack="feather"
                    icon="icon-eye"
                    class="mr-2"
                    :disabled="busyRow(r)"
                    @click="startRead(r)">{{ r.draft.status === 'failed' ? 'Read again' : 'Read' }}</vs-button>
                  <vs-button size="small" type="border" icon-pack="feather" icon="icon-plus" :disabled="busyRow(r)" @click="pickFiles(r)">
                    Add files
                  </vs-button>
                </template>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </hv-section>

      <hv-section
        v-if="recent.length"
        key="recent"
        title="Encoded in the last 14 days"
        sub="Receptions created on this page.">
        <div v-for="x in recent" :key="x.id" class="apv-recent">
          <span class="hv2-code">{{ x.apv_code }}</span>
          <feather-icon icon="ArrowRightIcon" svgClasses="h-3 w-3" class="mx-2" />
          <router-link :to="`/reception/${x.idoperation}`">Reception {{ x.reception_name || x.idoperation }}</router-link>
          <span class="hv2-muted ml-2">{{ dateTime(x.validated_at) }}</span>
        </div>
      </hv-section>
    </div>

    <input
      ref="file"
      type="file"
      multiple
      accept="application/pdf,image/jpeg,image/png"
      style="display: none"
      @change="onFiles" />

    <!-- Toujours monté (jamais en v-if) : Vuesax le déplace dans <body>. -->
    <vs-popup :active.sync="reviewOpen" :title="reviewTitle" fullscreen class="apv-review-popup">
      <div class="hv2 apv-review">
        <div v-if="reviewLoading" key="rl" class="hv2-loading">
          <vs-progress indeterminate color="primary" class="mb-2" />
          Loading the contract…
        </div>
        <div v-else-if="review" key="rv" class="apv-review-grid">
          <!-- Documents -->
          <div class="apv-docs">
            <div class="apv-doc-tabs">
              <button
                v-for="(d, i) in docs"
                :key="d.id"
                type="button"
                class="apv-doc-tab"
                :class="{ 'is-active': i === activeDoc }"
                @click="activeDoc = i">
                File {{ i + 1 }} · {{ d.name }}
              </button>
              <vs-button
                v-if="!validated"
                size="small"
                type="flat"
                icon-pack="feather"
                icon="icon-plus"
                @click="addPagesFromReview">Add files</vs-button>
            </div>
            <div class="apv-doc-view">
              <div v-if="!docs.length" class="hv2-muted">No document.</div>
              <template v-else-if="docs[activeDoc]">
                <div v-if="!docs[activeDoc].url" class="hv2-muted">Loading the document…</div>
                <iframe
                  v-else-if="docs[activeDoc].mime === 'application/pdf'"
                  :src="docs[activeDoc].url"
                  class="apv-pdf"
                  title="Contract document"></iframe>
                <img v-else :src="docs[activeDoc].url" class="apv-img" alt="Contract page" />
              </template>
            </div>
          </div>

          <!-- La réception proposée -->
          <div class="apv-form">
            <div v-if="validated" class="apv-done">
              <feather-icon icon="CheckCircleIcon" svgClasses="h-5 w-5" />
              <div>
                <b>Reception {{ validated.reception_name }} created.</b>
                <div>
                  <router-link :to="`/reception/${validated.idoperation}`" @click.native="reviewOpen = false">Open the reception</router-link>
                </div>
              </div>
            </div>

            <div v-if="checks.length && !validated" class="apv-checks">
              <div v-for="(c, i) in checks" :key="i" class="apv-check" :class="`is-${c.level}`">
                <feather-icon :icon="checkIcon(c.level)" svgClasses="h-4 w-4" />
                <span>{{ c.message }}</span>
              </div>
            </div>

            <div class="hv2-kv">
              <div class="hv2-kv-k">Supplier</div>
              <vs-select
                v-model="form.idperson"
                class="w-full"
                autocomplete
                :disabled="!!validated"
                placeholder="Choose the supplier">
                <vs-select-item v-for="c in supplierOptions" :key="c.id" :value="c.id" :text="c.text" />
              </vs-select>
              <div class="apv-read">
                Read in the contract: <b>{{ values.seller_name || 'nothing' }}</b>
                <template v-if="values.seller_cui"> · CUI {{ values.seller_cui }}</template>
                <a v-if="!showAllPeople && !validated" href="#" class="ml-2" @click.prevent="loadAllPeople">Choose another contact</a>
              </div>
            </div>

            <div class="hv2-kv">
              <div class="hv2-kv-k">Contract</div>
              <div class="apv-two">
                <vs-input v-model="form.contract_number" class="w-full" label="Number" :disabled="!!validated" />
                <vs-input v-model="form.contract_date" class="w-full" type="date" label="Date" :disabled="!!validated" />
              </div>
              <div class="apv-read">On the reception (Contract No): <b>{{ values.invoice_no || DASH }}</b></div>
              <div v-if="evidence('contract_number')" class="apv-quote">{{ evidence('contract_number') }}</div>
            </div>

            <div class="hv2-kv">
              <div class="hv2-kv-k">APV</div>
              <div>
                <span class="hv2-code apv-strong">{{ review.draft.apv_code }}</span>
                <hv-pill
                  :label="review.proposal.lot_found ? 'found in the contract' : 'not found in the contract'"
                  :tone="review.proposal.lot_found ? 'ok' : 'danger'"
                  class="ml-2" />
                <hv-pill v-if="review.in_sumal === false" label="not in SUMAL yet" tone="warn" class="ml-2" />
              </div>
              <div v-if="review.in_sumal === false && !validated" class="apv-read">
                Product to create: <b>{{ review.product_label }}</b> — SUMAL will link to it by the APV number once it shows this APV.
              </div>
              <vs-checkbox
                v-if="!review.proposal.lot_found && !validated"
                v-model="form.confirm_apv"
                class="mt-2">This contract is the right one for APV {{ review.draft.apv_code }}</vs-checkbox>
              <div v-if="review.product_name" class="apv-read">Product: {{ review.product_name }}</div>
            </div>

            <div class="hv2-kv">
              <div class="hv2-kv-k">Parcel and volume</div>
              <div class="apv-three">
                <vs-input v-model="form.partida" class="w-full" label="Partida" :disabled="!!validated" />
                <vs-input v-model="form.volume_m3" class="w-full" type="number" step="0.01" label="Volume (m³)" :disabled="!!validated" />
                <vs-input v-model="form.unit_price_ron" class="w-full" type="number" step="0.01" label="Price (RON/m³)" :disabled="!!validated" />
              </div>
              <div class="apv-read">
                {{ basisLine }}
                <template v-if="eurPerM3"> · ≈ {{ eur(eurPerM3) }} €/m³ at today's ECB rate ({{ rateText }})</template>
              </div>
              <div class="apv-read">
                Total: <b>{{ ron(values.total_ron) }} RON</b>
                <template v-if="values.contract_total_ron"> · contract: {{ ron(values.contract_total_ron) }} RON</template>
                <template v-if="review.parcel"> · SUMAL: {{ m3(review.parcel.m3_out) }} m³ out, {{ m3(review.parcel.sumal_stock_m3) }} m³ in stock</template>
              </div>
              <div v-if="review.proposal.evidence && review.proposal.evidence.lot" class="apv-quote">
                “{{ review.proposal.evidence.lot.quote }}” — file {{ review.proposal.evidence.lot.file_index }}, page {{ review.proposal.evidence.lot.page }}
              </div>
            </div>

            <div class="hv2-kv">
              <div class="hv2-kv-k">Forest</div>
              <div class="flex items-center">
                <vs-switch v-model="form.isapvprivate" :disabled="!!validated" />
                <span class="ml-3">{{ form.isapvprivate ? 'Private APV' : 'Public APV' }}</span>
              </div>
              <div v-if="evidence('forest_ownership')" class="apv-quote">{{ evidence('forest_ownership') }}</div>
            </div>

            <div class="hv2-kv">
              <div class="hv2-kv-k">Reception date</div>
              <div>{{ date(values.date) }} <span class="hv2-muted">(today, like every APV reception)</span></div>
            </div>

            <div v-if="hasOther" class="hv2-kv">
              <div class="hv2-kv-k">Also in the contract (not encoded)</div>
              <div v-for="o in otherItems" :key="o.key" class="apv-other">
                <span class="hv2-muted apv-other-k">{{ o.label }}:</span>{{ o.value }}
              </div>
            </div>

            <div v-if="review.reusable_apvs && review.reusable_apvs.length" class="hv2-kv">
              <div class="hv2-kv-k">Other APVs in this contract</div>
              <div v-for="a in review.reusable_apvs" :key="a" class="apv-other">
                <span class="hv2-code">{{ a }}</span>
                <vs-button size="small" type="border" class="ml-2" :disabled="reusing === a" @click="reuseFor(a)">
                  Use this contract for this APV too
                </vs-button>
              </div>
            </div>

            <div class="apv-actions">
              <vs-button
                v-if="!validated"
                color="success"
                icon-pack="feather"
                icon="icon-check"
                :disabled="saving || previewBusy || review.proposal.blocked"
                @click="confirm">{{ saving ? 'Creating…' : 'Confirm and create the reception' }}</vs-button>
              <vs-button v-if="!validated" type="border" class="ml-2" :disabled="saving" @click="rereadFromReview">Read again</vs-button>
              <vs-button v-if="!validated" type="flat" color="danger" class="ml-2" :disabled="saving" @click="discard">Discard</vs-button>
              <vs-button type="flat" class="ml-2" @click="reviewOpen = false">Close</vs-button>
            </div>
            <div class="hv2-muted mt-2">{{ readLine }}</div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import axios from '@/axios.js'
import HvSection from './components/HvSection'
import HvPill from './components/HvPill'
import HvEmpty from './components/HvEmpty'
import formatMixin from './formatMixin'

const POLL_MS = 4000

export default {
  name: 'ApvEncode',
  components: { HvSection, HvPill, HvEmpty },
  mixins: [formatMixin],
  data () {
    return {
      rows: [],
      recent: [],
      loading: false,
      error: '',
      uploading: {},
      uploadTarget: null,
      reading: {},
      pollTimer: null,
      // volet de confirmation
      reviewOpen: false,
      reviewLoading: false,
      review: null,
      reviewRow: null,
      initial: {},
      form: {},
      docs: [],
      activeDoc: 0,
      previewBusy: false,
      previewTimer: null,
      saving: false,
      validated: null,
      reusing: null,
      formReady: false,
      allPeople: [],
      showAllPeople: false,
      eurRate: null
    }
  },
  computed: {
    // L'APV d'où vient le clic « Encode » (Harvesting 2.0), s'il y en a un.
    focus () { return String(this.$route.query.apv || '') },
    subtitle () {
      const n = this.rows.length
      return `${n} APV${n === 1 ? '' : 's'} without a purchase reception, as in the Harvesting morning report. ` +
        'Upload the contract of an APV: Claude reads it and prepares the reception, which you check and confirm. ' +
        '“New contract” starts from the contract when SUMAL does not show the APV yet.'
    },
    reviewTitle () {
      if (!this.review) return 'Contract'
      return this.review.draft.apv_code ? `APV ${this.review.draft.apv_code} — check and confirm the reception` : 'Contract'
    },
    values () {
      return (this.review && this.review.proposal && this.review.proposal.values) || {}
    },
    checks () {
      return (this.review && this.review.proposal && this.review.proposal.checks) || []
    },
    supplierOptions () {
      const out = []
      const seen = new Set()
      const cands = (this.review && this.review.supplier && this.review.supplier.candidates) || []
      cands.forEach((c) => {
        seen.add(c.id)
        const why = this.reasonText(c.reasons)
        out.push({ id: c.id, text: `${c.name}${c.cui ? ` · ${c.cui}` : ''}${why ? ` — ${why}` : ''}` })
      })
      if (this.showAllPeople) {
        this.allPeople.forEach((p) => { if (!seen.has(p.id)) out.push({ id: p.id, text: p.name }) })
      } else if (this.form.idperson && !seen.has(this.form.idperson)) {
        out.push({ id: this.form.idperson, text: `Contact #${this.form.idperson}` })
      }
      return out
    },
    eurPerM3 () {
      const p = Number(this.form.unit_price_ron)
      return p > 0 && this.eurRate ? p / Number(this.eurRate.toFixed(4)) : null
    },
    rateText () {
      return this.eurRate ? this.eurRate.toFixed(4) : ''
    },
    basisLine () {
      const v = this.values
      const basis = { gross: 'gross volume', net: 'net volume' }[v.volume_basis] || 'volume basis not stated'
      const parts = [`Price applied to the ${basis}`]
      if (v.gross_m3) parts.push(`gross ${this.m3(v.gross_m3)} m³`)
      if (v.net_m3) parts.push(`net ${this.m3(v.net_m3)} m³`)
      return parts.join(' · ')
    },
    otherItems () {
      const o = (this.review && this.review.proposal && this.review.proposal.other) || {}
      return [
        { key: 'guarantee', label: 'Guarantee', item: o.guarantee },
        { key: 'payment_terms', label: 'Payment terms', item: o.payment_terms },
        { key: 'harvest_deadline', label: 'Harvest deadline', item: o.harvest_deadline }
      ].filter((x) => x.item && x.item.value).map((x) => ({ key: x.key, label: x.label, value: x.item.value }))
    },
    hasOther () {
      return this.otherItems.length > 0
    },
    readLine () {
      const d = this.review && this.review.draft
      if (!d) return ''
      const u = d.ai_usage || {}
      const tokens = Number(u.input_tokens || 0) + Number(u.output_tokens || 0)
      return `Read by ${d.ai_model || 'Claude'}${d.read_finished_at ? ` on ${this.dateTime(d.read_finished_at)}` : ''}` +
        `${tokens ? ` · ${this.int(tokens)} tokens` : ''}.`
    }
  },
  methods: {
    notify (title, text, color) {
      this.$vs.notify({
        title,
        text,
        color,
        iconPack: 'feather',
        icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check',
        position: 'bottom-right',
        time: 7000
      })
    },
    errorText (e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Error'
    },

    /* ----------------------------------------------------------- la liste */
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get('/apv-contracts')
        this.rows = this.focusFirst(data.rows || [])
        this.recent = data.recent || []
        this.rows.forEach((r) => { if (r.draft && r.draft.status === 'reading') this.trackReading(r.draft.id) })
      } catch (e) {
        this.error = this.errorText(e)
      } finally {
        this.loading = false
      }
    },
    focusFirst (rows) {
      const i = rows.findIndex((r) => r.apv_code === this.focus)
      return i > 0 ? [rows[i], ...rows.slice(0, i), ...rows.slice(i + 1)] : rows
    },
    placeLine (r) {
      return [r.county, r.usual_supplier ? `usually ${r.usual_supplier}` : ''].filter(Boolean).join(' · ')
    },
    deadlineText (d) {
      if (d === null || d === undefined) return this.DASH
      if (d < 0) return `${-d} day${d === -1 ? '' : 's'} overdue`
      if (d === 0) return 'today'
      return `in ${d} day${d === 1 ? '' : 's'}`
    },
    deadlineClass (d) {
      if (d === null || d === undefined) return 'hv2-muted'
      if (d <= 15) return 'apv-due is-danger'
      if (d <= 30) return 'apv-due is-warn'
      return 'apv-due'
    },
    statusLabel (r) {
      const s = r.draft && r.draft.status
      return {
        uploaded: 'Documents uploaded',
        reading: 'Reading…',
        ready: 'Ready to confirm',
        failed: 'Reading failed'
      }[s] || 'No contract'
    },
    statusTone (r) {
      const s = r.draft && r.draft.status
      return { uploaded: 'info', reading: 'info', ready: 'warn', failed: 'danger' }[s] || ''
    },
    statusIcon (r) {
      const s = r.draft && r.draft.status
      return { uploaded: 'FileIcon', reading: 'LoaderIcon', ready: 'CheckSquareIcon', failed: 'AlertTriangleIcon' }[s] || 'FileMinusIcon'
    },
    filesLine (d) {
      const n = d.files.length
      return `${n} file${n === 1 ? '' : 's'}`
    },
    busyRow (r) {
      return this.uploading[this.rowKey(r)] !== undefined || !!(r.draft && this.reading[r.draft.id])
    },
    /** Une ligne = un APV, ou un contrat chargé sans code (tant que Claude ne l'a pas lu). */
    rowKey (r) {
      return r.apv_code || `draft-${r.draft ? r.draft.id : 'new'}`
    },
    rowOf (apv) {
      return this.rows.find((r) => r.apv_code === apv)
    },
    /** La ligne d'un brouillon : par son id d'abord (ligne sans code), sinon par l'APV. */
    setDraft (draft) {
      const row = this.rows.find((r) => r.draft && r.draft.id === draft.id) || (draft.apv_code ? this.rowOf(draft.apv_code) : null)
      if (!row) return
      this.$set(row, 'draft', draft)
      // Le contrat vient d'être lu : la ligne prend le code que Claude y a trouvé.
      if (!row.apv_code && draft.apv_code) this.$set(row, 'apv_code', draft.apv_code)
    },

    /* ------------------------------------------------- envoi des documents */
    /** `row` = la ligne à compléter ; `null` = « New contract » (le code viendra du contrat). */
    pickFiles (row) {
      this.uploadTarget = row
      this.$refs.file.value = ''
      this.$refs.file.click()
    },
    async onFiles (e) {
      const files = Array.from((e.target && e.target.files) || [])
      const row = this.uploadTarget
      if (!files.length) return
      const key = row ? this.rowKey(row) : 'new'
      const fd = new FormData()
      // Un brouillon sans code se complète par son id (avant les fichiers, pour multer).
      if (row && !row.apv_code && row.draft) fd.append('draft_id', String(row.draft.id))
      files.forEach((f) => fd.append('files', f, f.name))
      const url = row && row.apv_code ? `/apv-contracts/${row.apv_code}/files` : '/apv-contracts/files'
      this.$set(this.uploading, key, 0)
      try {
        const { data } = await axios.post(url, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
          onUploadProgress: (ev) => {
            if (ev.total) this.$set(this.uploading, key, Math.round((ev.loaded / ev.total) * 100))
          }
        })
        if (row) {
          this.setDraft(data.draft)
        } else {
          // Nouveau contrat : une ligne en tête, sans code, comme la liste la rendra.
          this.rows.unshift({
            apv_code: null,
            label: '',
            product_name: null,
            not_encoded: true,
            contract_only: true,
            in_sumal: false,
            county: null,
            locality: null,
            avize: 0,
            m3_out: 0,
            sumal_stock_m3: null,
            days_to_expiry: null,
            deadline: null,
            usual_supplier: null,
            draft: data.draft
          })
        }
        this.$delete(this.uploading, key)
        // Le volet de confirmation était ouvert sur ce contrat : il montrera la nouvelle lecture.
        if (this.reviewOpen && this.review && this.review.draft.id === data.draft.id) this.reviewOpen = false
        await this.startRead({ apv_code: data.draft.apv_code, draft: data.draft })
      } catch (err) {
        this.$delete(this.uploading, key)
        this.notify('Upload', this.errorText(err), 'danger')
      }
    },

    /* -------------------------------------------------------- la lecture */
    async startRead (row) {
      const id = row.draft && row.draft.id
      if (!id) return
      this.$set(this.reading, id, true)
      try {
        const { data } = await axios.post(`/apv-contracts/${id}/read`)
        this.setDraft(data.draft)
        this.trackReading(id)
      } catch (err) {
        this.$delete(this.reading, id)
        this.notify('Read', this.errorText(err), 'danger')
      }
    },
    trackReading (id) {
      this.$set(this.reading, id, true)
      if (!this.pollTimer) this.pollTimer = setTimeout(this.poll, POLL_MS)
    },
    async poll () {
      this.pollTimer = null
      const ids = Object.keys(this.reading)
      for (const id of ids) {
        try {
          const { data } = await axios.get(`/apv-contracts/${id}`)
          const d = data.draft
          const row = this.rows.find((r) => r.draft && r.draft.id === d.id)
          this.setDraft(d)
          if (d.status !== 'reading') {
            this.$delete(this.reading, id)
            const who = d.apv_code ? `APV ${d.apv_code}` : 'Contract'
            if (d.status === 'ready') this.notify('Contract read', `${who}: ready to check and confirm.`, 'success')
            if (d.status === 'failed') this.notify('Reading failed', `${who}: ${d.error || 'see the list.'}`, 'danger')
            // Contrat chargé d'abord : la liste sait maintenant la parcelle lue et si SUMAL connaît l'APV.
            if (row && row.contract_only && d.status === 'ready') this.load()
          }
        } catch (e) {
          // une erreur passagère ne doit pas arrêter le suivi
        }
      }
      if (Object.keys(this.reading).length) this.pollTimer = setTimeout(this.poll, POLL_MS)
    },

    /* --------------------------------------------- le volet de confirmation */
    async openReview (row) {
      this.reviewRow = row
      this.reviewOpen = true
      this.reviewLoading = true
      this.review = null
      this.validated = null
      this.showAllPeople = false
      this.clearDocs()
      try {
        const { data } = await axios.get(`/apv-contracts/${row.draft.id}`)
        if (!data.proposal) {
          this.reviewOpen = false
          this.notify('Contract', 'This contract has not been read yet.', 'danger')
          return
        }
        this.review = data
        this.initForm(data.proposal.values)
        this.loadDocs(data.draft)
        this.loadRate()
      } catch (e) {
        this.reviewOpen = false
        this.notify('Contract', this.errorText(e), 'danger')
      } finally {
        this.reviewLoading = false
      }
    },
    initForm (v) {
      const form = {
        idperson: v.idperson || null,
        contract_number: v.contract_number || '',
        contract_date: v.contract_date || '',
        partida: v.partida || '',
        volume_m3: v.volume_m3 ? String(v.volume_m3) : '',
        unit_price_ron: v.unit_price_ron ? String(v.unit_price_ron) : '',
        isapvprivate: !!v.isapvprivate,
        confirm_apv: false
      }
      this.formReady = false
      this.initial = { ...form }
      this.form = form
      // Les corrections ne comptent qu'après le remplissage initial.
      this.$nextTick(() => { this.formReady = true })
    },
    reasonText (reasons) {
      return (reasons || []).map((r) => {
        if (r === 'cui') return 'CUI matches'
        if (r === 'ocol') return 'same forest district'
        if (r.indexOf('name ') === 0) return 'similar name'
        if (r.indexOf('history ') === 0) {
          const n = Number(r.slice(8))
          return `${n} earlier reception${n === 1 ? '' : 's'} of this ocol`
        }
        return r
      }).join(', ')
    },
    /** Seulement ce que Florent a changé : le serveur recalcule tout le reste. */
    edits () {
      const out = {}
      Object.keys(this.form).forEach((k) => {
        if (k === 'confirm_apv') { if (this.form.confirm_apv) out.confirm_apv = true; return }
        if (String(this.form[k]) !== String(this.initial[k])) out[k] = this.form[k]
      })
      return out
    },
    onEdit () {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(this.preview, 500)
    },
    async preview () {
      if (!this.review) return
      this.previewBusy = true
      try {
        const { data } = await axios.post(`/apv-contracts/${this.review.draft.id}/preview`, { edits: this.edits() })
        this.review = { ...this.review, proposal: data.proposal, supplier: data.supplier }
      } catch (e) {
        this.notify('Check', this.errorText(e), 'danger')
      } finally {
        this.previewBusy = false
      }
    },
    evidence (key) {
      const ev = this.review && this.review.proposal && this.review.proposal.evidence && this.review.proposal.evidence[key]
      if (!ev || !ev.quote) return ''
      return `“${ev.quote}” — file ${ev.file_index}, page ${ev.page}`
    },
    checkIcon (level) {
      return { block: 'XCircleIcon', warn: 'AlertTriangleIcon', info: 'InfoIcon' }[level] || 'InfoIcon'
    },
    async loadRate () {
      try {
        const { data } = await axios.get('/currency/ecb')
        const r = parseFloat(data && data.eur_ron)
        this.eurRate = r > 0 ? r : null
      } catch (e) {
        this.eurRate = null
      }
    },
    async loadAllPeople () {
      try {
        const { data } = await axios.get('/people_name/data')
        this.allPeople = (data || []).map((p) => ({ id: p.value, name: p.label || p.text }))
        this.showAllPeople = true
      } catch (e) {
        this.notify('Contacts', this.errorText(e), 'danger')
      }
    },

    /* ------------------------------------------------ documents (aperçu) */
    clearDocs () {
      this.docs.forEach((d) => { if (d.url) URL.revokeObjectURL(d.url) })
      this.docs = []
      this.activeDoc = 0
    },
    loadDocs (draft) {
      this.docs = (draft.files || []).map((f) => ({ ...f, url: '' }))
      this.docs.forEach((d, i) => {
        // Avec le jeton : une balise <img>/<iframe> ne l'enverrait pas.
        axios.get(`/apv-contracts/${draft.id}/files/${d.id}`, { responseType: 'blob', timeout: 120000 })
          .then(({ data }) => {
            if (this.docs[i] && this.docs[i].id === d.id) this.$set(this.docs, i, { ...this.docs[i], url: URL.createObjectURL(data) })
          })
          .catch(() => { /* l'onglet reste « Loading » ; le reste du volet fonctionne */ })
      })
    },
    addPagesFromReview () {
      if (this.reviewRow) this.pickFiles(this.reviewRow)
    },

    /* ------------------------------------------------------ les décisions */
    async confirm () {
      if (!this.review || this.saving) return
      this.saving = true
      try {
        const { data } = await axios.post(`/apv-contracts/${this.review.draft.id}/validate`, {
          edits: this.edits(),
          read_nonce: this.review.draft.read_nonce
        }, { timeout: 120000 })
        this.validated = data
        this.notify('Reception created', `Reception ${data.reception_name} for APV ${this.review.draft.apv_code}.`, 'success')
        this.load()
      } catch (e) {
        const blocks = e && e.response && e.response.data && e.response.data.checks
        if (blocks && blocks.length) {
          this.review = { ...this.review, proposal: { ...this.review.proposal, checks: blocks, blocked: true } }
        }
        this.notify('Not created', this.errorText(e), 'danger')
      } finally {
        this.saving = false
      }
    },
    async rereadFromReview () {
      const row = this.reviewRow
      this.reviewOpen = false
      if (row) await this.startRead(row)
    },
    async discard () {
      if (!this.review) return
      try {
        await axios.post(`/apv-contracts/${this.review.draft.id}/discard`)
        this.reviewOpen = false
        const code = this.review.draft.apv_code
        this.notify('Discarded', code ? `The contract of APV ${code} was set aside.` : 'The contract was set aside.', 'success')
        this.load()
      } catch (e) {
        this.notify('Discard', this.errorText(e), 'danger')
      }
    },
    async reuseFor (apv) {
      if (!this.review) return
      this.reusing = apv
      try {
        await axios.post(`/apv-contracts/${this.review.draft.id}/reuse/${apv}`)
        this.notify('Prepared', `APV ${apv} is ready to confirm with the same contract.`, 'success')
        await this.load()
        this.review = { ...this.review, reusable_apvs: this.review.reusable_apvs.filter((a) => a !== apv) }
      } catch (e) {
        this.notify('Not prepared', this.errorText(e), 'danger')
      } finally {
        this.reusing = null
      }
    }
  },
  watch: {
    focus () {
      this.rows = this.focusFirst(this.rows)
    },
    reviewOpen (open) {
      if (!open) this.clearDocs()
    },
    form: {
      deep: true,
      handler () {
        if (this.formReady && !this.validated) this.onEdit()
      }
    }
  },
  created () {
    // Session expirée (ou vidée par le contrôle des 23 h) : à la connexion, en
    // gardant cette page. Sans ce cas, le contrôle de rôle ci-dessous renvoyait
    // au tableau de bord et le lien du mail était perdu.
    if (!localStorage.getItem('accessToken')) {
      this.$router.push({ path: '/pages/login', query: { to: this.$route.fullPath } }).catch(() => {})
      return
    }
    const roles = String(localStorage.getItem('login-roles') || '').toLowerCase()
    if (!roles.includes('admin') && !roles.includes('moderator')) {
      this.notify('Denied', 'This page is for administrators and moderators.', 'danger')
      this.$router.push({ path: '/dashboard/analytics' })
      return
    }
    this.load()
  },
  beforeDestroy () {
    if (this.pollTimer) clearTimeout(this.pollTimer)
    if (this.previewTimer) clearTimeout(this.previewTimer)
    this.clearDocs()
  }
}
</script>

<style lang="scss">
/* Non scopé, préfixé .hv2 : la page vit hors du layout /harvesting2 qui charge d'habitude cette feuille. */
@import "@/assets/scss/modules/harvesting2.scss";
</style>

<style lang="scss" scoped>
// Assez précis pour passer devant le fond transparent des lignes hv2.
.hv2 .vs-con-table .vs-table--tbody-table tr.apv-focus {
  background: var(--hv-ok-tint);
}
.apv-strong {
  font-weight: 600;
  color: var(--hv-ink);
}
.apv-due {
  color: var(--hv-body);
}
.apv-due.is-warn {
  color: var(--hv-warn);
  font-weight: 600;
}
.apv-due.is-danger {
  color: var(--hv-danger);
  font-weight: 600;
}
.apv-error-line {
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--hv-danger);
  max-width: 280px;
  white-space: normal;
}
.apv-recent {
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--hv-rule);
}
.apv-review-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(360px, 0.9fr);
  gap: 24px;
}
@media (max-width: 1000px) {
  .apv-review-grid {
    grid-template-columns: 1fr;
  }
}
.apv-doc-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.apv-doc-tab {
  border: 1px solid var(--hv-rule-strong);
  background: transparent;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78rem;
  color: var(--hv-body);
  cursor: pointer;
}
.apv-doc-tab.is-active {
  background: var(--hv-sage-tint);
  border-color: var(--hv-sage);
  color: var(--hv-sage-ink);
  font-weight: 600;
}
.apv-doc-view {
  min-height: 60vh;
  background: var(--hv-surface);
  border-radius: 8px;
  padding: 8px;
}
.apv-pdf {
  width: 100%;
  height: 78vh;
  border: 0;
}
.apv-img {
  display: block;
  max-width: 100%;
  margin: 0 auto;
}
.apv-checks {
  margin-bottom: 12px;
}
.apv-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 0.82rem;
  line-height: 1.45;
}
.apv-check.is-block {
  background: var(--hv-danger-tint);
  color: var(--hv-danger);
}
.apv-check.is-warn {
  background: var(--hv-warn-tint);
  color: var(--hv-warn);
}
.apv-check.is-info {
  background: var(--hv-info-tint);
  color: var(--hv-info);
}
.apv-read {
  margin-top: 6px;
  font-size: 0.82rem;
  color: var(--hv-body);
}
.apv-quote {
  margin-top: 4px;
  font-size: 0.76rem;
  font-style: italic;
  color: var(--hv-muted);
}
.apv-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.apv-three {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}
.apv-other-k {
  margin-right: 4px;
}
.apv-other {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 4px;
  font-size: 0.85rem;
}
.apv-actions {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.apv-done {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 12px;
  background: var(--hv-ok-tint);
  color: var(--hv-ok);
}
</style>
